import { useEffect, useRef, useState } from 'react';
import type { Prospect, ProspectHarvestDetail } from '@prototypical/types';
import { api } from '../../../lib/api';
import { friendlyError } from '../../../lib/friendlyError';
import { saveDownload } from './savedHarvests';
import HarvestFacts from './HarvestFacts';

export default function ProspectDetails({ placeId, currentProspect, onClose }: { placeId: string; currentProspect?: Prospect; onClose: () => void }) {
  const [detail, setDetail] = useState<ProspectHarvestDetail | null>(null);
  const [error, setError] = useState('');
  const [retry, setRetry] = useState(0);
  const location = currentProspect?.location ?? detail?.prospect.location;
  useEffect(() => {
    const controller = new AbortController(); setDetail(null); setError('');
    api.prospectHarvestDetail(placeId, controller.signal).then(value => { if (!controller.signal.aborted) setDetail(value); }).catch(reason => { if (!controller.signal.aborted) setError(friendlyError(reason, 'Saved details could not load. Please retry.')); });
    return () => controller.abort();
  }, [placeId, retry]);
  return <section className="prospect-detail" aria-label="Saved business details">
    <header><div><h2>{detail?.prospect.name ?? 'Saved business details'}</h2><p className="muted">Stored harvest data</p></div><button className="btn" onClick={onClose}>Close details</button></header>
    {!detail && !error && <p role="status">Loading saved details…</p>}
    {error && <div role="alert"><p>{error}</p><button className="btn" onClick={() => setRetry(value => value + 1)}>Retry details</button></div>}
    {detail && <>
      <p>{detail.prospect.address || 'Address not available'}</p>
      <dl className="prospect-detail-summary"><dt>Place ID</dt><dd>{detail.prospect.placeId}</dd><dt>Harvested</dt><dd>{detail.prospect.harvestedAt ? new Date(detail.prospect.harvestedAt).toLocaleString() : 'Not recorded'}</dd><dt>Website</dt><dd>{detail.prospect.website || 'Not recorded'}</dd><dt>Location</dt><dd>{location ? `${location.lat}, ${location.lng}` : 'Not recorded'}</dd></dl>
      <HarvestFacts detail={detail} />
      <h3>Photos ({detail.photos.length})</h3>
      {!detail.photoDownloadsConfigured && <p className="muted">Photo downloads are not configured for this environment.</p>}
      {!detail.photos.length && <p className="muted">No photos were saved with this harvest.</p>}
      <div className="prospect-photo-list">{detail.photos.map(photo => <SavedPhoto key={`${placeId}:${photo.id}`} placeId={placeId} photo={photo} />)}</div>
      <details className="prospect-data"><summary>Full business data (JSON)</summary><pre>{JSON.stringify(detail.data, null, 2)}</pre></details>
      {Object.entries(detail.collections).map(([name, rows]) => <details className="prospect-data" key={name}><summary>Full {name} data ({rows.length})</summary><pre>{JSON.stringify(rows, null, 2)}</pre></details>)}
    </>}
  </section>;
}

function SavedPhoto({ placeId, photo }: { placeId: string; photo: ProspectHarvestDetail['photos'][number] }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [preview, setPreview] = useState<string | null>(null);
  const active = useRef<AbortController | null>(null);
  useEffect(() => () => active.current?.abort(), []);
  useEffect(() => () => { if (preview) URL.revokeObjectURL(preview); }, [preview]);
  async function photoAction(download: boolean) {
    if (active.current) return;
    const controller = new AbortController(); active.current = controller; setBusy(true); setError('');
    try {
      const blob = await api.prospectPhoto(placeId, photo.id, controller.signal);
      if (controller.signal.aborted) return;
      if (download) saveDownload(blob, photo.filename);
      else setPreview(URL.createObjectURL(blob));
    } catch (reason) { if (!controller.signal.aborted) setError(friendlyError(reason, 'Photo could not load. Please retry.')); }
    finally { active.current = null; if (!controller.signal.aborted) setBusy(false); }
  }
  return <div className="prospect-photo"><div><strong>{photo.filename}</strong><span>{photo.category}</span></div>
    {photo.downloadAvailable ? <div className="prospect-photo-actions"><button className="btn" disabled={busy} onClick={() => preview ? setPreview(null) : void photoAction(false)}>{preview ? 'Hide photo' : 'View photo'}</button><button className="btn" disabled={busy} onClick={() => void photoAction(true)}>Download photo</button></div> : <p className="muted">Photo file unavailable</p>}
    {busy && <p role="status">Loading photo…</p>}{preview && <img src={preview} alt={`${photo.category}: ${photo.filename}`} />}{error && <p role="alert">{error}</p>}
  </div>;
}
